import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import CustomerFields, {
  customerTargets,
} from "@/components/forms/customer-fields";
import { FormErrors } from "@/components/forms/form-errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  locationCustomersPath,
  locationCustomerPath,
  locationCustomerReusesPath,
} from "@/routes";
import type { CustomerProfile, CustomerMatch } from "./types";

export default function CustomerForm({
  customer,
  matches = [],
  matching_contacts = {},
}: {
  customer: CustomerProfile | null;
  matches: CustomerMatch[];
  matching_contacts: { email?: string; phone?: string };
}) {
  const { auth, errors: pageErrors } = usePage<{
    errors: Record<string, string>;
  }>().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(customer ? "customers.edit" : "customers.create");
  const indexPath = locationCustomersPath(locationId);
  return (
    <AppLayout breadcrumbs={[{ title: t("customers.title"), href: indexPath }]}>
      <Head title={title} />
      <PageCanvas>
        <h1 className="text-page font-display">{title}</h1>
        <Card>
          <CardHeader>
            <CardTitle>{t("customers.details")}</CardTitle>
          </CardHeader>
          <CardContent>
            <FormErrors errors={pageErrors || {}} targets={customerTargets} />
            <Form
              key={customer?.revision || "new"}
              action={
                customer
                  ? locationCustomerPath(locationId, customer.id)
                  : indexPath
              }
              method={customer ? "patch" : "post"}
            >
              {({ errors, processing }) => (
                <div className="space-y-4">
                  {customer && (
                    <input
                      type="hidden"
                      name="revision"
                      value={customer.revision}
                    />
                  )}
                  <CustomerFields
                    values={{
                      name: customer?.name || "",
                      email: customer?.email || "",
                      phone: customer?.phone || "",
                      address: customer?.address || "",
                    }}
                    errors={{ ...pageErrors, ...errors }}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t(customer ? "customers.save" : "customers.create")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={
                            customer
                              ? locationCustomerPath(locationId, customer.id)
                              : indexPath
                          }
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
        {matches.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t("customers.choose")}</CardTitle>
              <p className="text-muted-foreground text-sm">
                {t("customers.reuse_help")}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {matches.map((match) => (
                <div key={match.id} className="space-y-3 rounded-lg border p-4">
                  <p className="font-medium">{match.name}</p>
                  <p className="text-muted-foreground text-sm">
                    {t("customers.matched")}:{" "}
                    {match.matched_contacts
                      .map((contact) => t(`customers.${contact}`))
                      .join(", ")}
                  </p>
                  <Form
                    action={locationCustomerReusesPath(locationId)}
                    method="post"
                  >
                    {({ processing }) => (
                      <>
                        <input
                          type="hidden"
                          name="identity_id"
                          value={match.id}
                        />
                        <input
                          type="hidden"
                          name="email"
                          value={matching_contacts.email || ""}
                        />
                        <input
                          type="hidden"
                          name="phone"
                          value={matching_contacts.phone || ""}
                        />
                        <Button
                          type="submit"
                          variant="outline"
                          disabled={processing}
                        >
                          {t("customers.use_saved")}
                        </Button>
                      </>
                    )}
                  </Form>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </PageCanvas>
    </AppLayout>
  );
}
